import HeaderSection from "@/components/Header/HeaderSection.vue";
import BolideAssistant from "@/components/BolideAssistant/BolideAssistant.vue";
import SettingsAssignmentModal from "@/components/SettingsAssignmentModal/SettingsAssignmentModal.vue";
import SettingsPropertyTab from "@/components/SettingsPropertyTab/SettingsPropertyTab.vue";
import SettingsServiceProviderTab from "@/components/SettingsServiceProviderTab/SettingsServiceProviderTab.vue";
import SettingsTariffBookTab from "@/components/SettingsTariffBookTab/SettingsTariffBookTab.vue";
import TariffBookRatesModal from "@/components/TariffBookRates/TariffBookRatesModal.vue";
import SnackbarMessage from "@/components/SnackbarMessage/SnackbarMessage.vue";
import PortfolioSidebar from "@/components/PortfolioSidebar/PortfolioSidebar.vue";
import PropertySidebar from "@/components/PropertySidebar/PropertySidebar.vue";
import sidebar_page_context from "@/components/sidebar_page_context";
import { get_user_profile, fetch_and_store_user_profile } from "@/router";
import settings_api_methods from "@/components/Settings/settings_api_methods";
import settings_ui_methods from "@/components/Settings/settings_ui_methods";

const settings_table_helpers = window.global_data_table?.create_component_helpers({
    selector: ".global_data_table"
}) || {};

export default {
    name: "SettingsSection",

    mixins: [sidebar_page_context],

    components: {
        HeaderSection,
        BolideAssistant,
        SettingsAssignmentModal,
        SettingsPropertyTab,
        SettingsServiceProviderTab,
        SettingsTariffBookTab,
        TariffBookRatesModal,
        SnackbarMessage,
        PortfolioSidebar,
        PropertySidebar
    },

    data() {
        return ({
            show_assistant: false,
            is_mobile: window.innerWidth < 768,
            mobile_sidebar_open: false,
            sidebar_collapsed: true,
            show_add_tariff_modal: false,
            show_tariff_rates_modal: false,
            selected_tariff_rates_tab: "view",
            is_loading: false,
            is_saving: false,
            active_settings_tab: "property",
            load_error: "",
            save_success: "",
            snackbar_timeout_id: null,
            form_error: "",
            selected_tariff_book: null,
            selected_tariff_context: null,
            breadcrumb: [
                { id: "home", title: "Dashboard" },
                { id: "settings", title: "Settings" }
            ],
            user_profile: null,
            municipalities: [],
            properties: [],
            tariff_books: [],
            tariffs: [],
            tariff_rates: [],
            property_tariff_assignments: [],
            selected_assignment_id: null,
            is_property_locked: false,
            active_property_context: null,
            new_tariff: {
                property_id: "",
                municipality_id: "",
                municipality_search: "",
                tariff_book_id: "",
                tariff_id: "",
                effective_from: "",
                effective_to: "",
                is_active: true
            },
            tariff_rows: []
        });
    },

    computed: {
        active_properties() {
            return this.visible_properties.filter((property) => property.is_active);
        },

        total_tariffs() {
            return this.visible_tariff_rows.length;
        },

        settings_tabs() {
            return [
                { id: "property", title: "Property" },
                { id: "tariff_book", title: "Tariff Book" },
                { id: "service_providers", title: "Service Providers" }
            ];
        },

        is_service_provider_tab() {
            return this.active_settings_tab === "service_providers";
        },

        active_service_provider_property_id() {
            const context = this.active_property_context;

            if (!context) {
                return "";
            }

            const matching_property = this.properties.find((property) => {
                return String(property.id) === String(context.id)
                    || String(property.name || "").toLowerCase() === String(context.title || "").toLowerCase();
            });

            return matching_property?.id || "";
        },

        active_settings_property_id() {
            return this.active_service_provider_property_id;
        },

        has_active_property_filter() {
            return Boolean(this.active_settings_property_id);
        },

        selected_context_property() {
            return this.properties.find((property) => {
                return String(property.id) === String(this.active_settings_property_id);
            }) || null;
        },

        visible_properties() {
            if (!this.has_active_property_filter) {
                return this.properties;
            }

            return this.properties.filter((property) => {
                return String(property.id) === String(this.active_settings_property_id);
            });
        },

        visible_tariff_rows() {
            if (!this.has_active_property_filter) {
                return this.tariff_rows;
            }

            return this.tariff_rows.filter((row) => {
                return String(row.property_id) === String(this.active_settings_property_id);
            });
        },

        visible_assigned_tariff_ids() {
            return new Set(
                this.visible_tariff_rows
                    .filter((row) => row.has_assignment && row.tariff_id)
                    .map((row) => Number(row.tariff_id))
            );
        },

        visible_tariff_book_ids() {
            const row_tariff_book_ids = this.visible_tariff_rows
                .filter((row) => row.has_assignment && row.tariff_book_id)
                .map((row) => Number(row.tariff_book_id));
            const related_tariff_book_ids = this.tariffs
                .filter((tariff) => this.visible_assigned_tariff_ids.has(Number(tariff.id)) && tariff.tariff_book_id)
                .map((tariff) => Number(tariff.tariff_book_id));

            return new Set(
                [
                    ...row_tariff_book_ids,
                    ...related_tariff_book_ids
                ].filter((tariff_book_id) => Number.isFinite(tariff_book_id))
            );
        },

        visible_tariff_book_tariff_ids() {
            return new Set(
                this.tariffs
                    .filter((tariff) => this.visible_tariff_book_ids.has(Number(tariff.tariff_book_id)))
                    .map((tariff) => Number(tariff.id))
                    .filter((tariff_id) => Number.isFinite(tariff_id))
            );
        },

        visible_tariff_books() {
            if (!this.has_active_property_filter) {
                return this.tariff_books;
            }

            return this.tariff_books.filter((tariff_book) => {
                return this.visible_tariff_book_ids.has(Number(tariff_book.id));
            });
        },

        visible_tariffs() {
            if (!this.has_active_property_filter) {
                return this.tariffs;
            }

            return this.tariffs.filter((tariff) => {
                return this.visible_assigned_tariff_ids.has(Number(tariff.id))
                    || this.visible_tariff_book_ids.has(Number(tariff.tariff_book_id));
            });
        },

        visible_tariff_rates() {
            if (!this.has_active_property_filter) {
                return this.tariff_rates;
            }

            return this.tariff_rates.filter((rate) => {
                return this.visible_tariff_book_tariff_ids.has(Number(rate.tariff_id));
            });
        },

        assigned_visible_tariffs_count() {
            return this.visible_tariff_rows.filter((row) => row.has_assignment).length;
        },

        tariff_categories_count() {
            return new Set(this.visible_tariff_rows.map((row) => row.tariff_category).filter(Boolean)).size;
        },

        tariff_types_count() {
            return new Set(this.visible_tariff_rows.map((row) => row.period).filter(Boolean)).size;
        },

        avatar_initials() {
            if (!this.user_profile) return "UR";

            const first = this.user_profile.first_name?.charAt(0) || "";
            const last = this.user_profile.last_name?.charAt(0) || "";

            return (first + last).toUpperCase() || "UR";
        },

        full_name() {
            if (!this.user_profile) return "User";

            const first = this.user_profile.first_name || "";
            const last = this.user_profile.last_name || "";

            return `${first} ${last}`.trim() || this.email_address;
        },

        email_address() {
            return this.user_profile?.email || "user@example.org";
        },

        user_role() {
            return this.user_profile?.role?.role || "User";
        }
    },

    async mounted() {
        window.addEventListener("resize", this.handle_resize);
        this.active_property_context = this.get_active_property_context();
        this.user_profile = get_user_profile() || await fetch_and_store_user_profile();
        await this.load_tariff_settings_data();
        settings_table_helpers.mounted?.call(this);
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.handle_resize);
        this.clear_snackbar_timer();
        settings_table_helpers.beforeUnmount?.call(this);
    },

    watch: {
        ...(settings_table_helpers.watch || {}),
        tariff_rows: {
            deep: true,
            handler() {
                if (this.active_settings_tab === "property") {
                    this.rebuild_tables?.();
                }
            }
        },
        tariff_books: {
            deep: true,
            handler() {
                if (this.active_settings_tab === "tariff_book") {
                    this.rebuild_tables?.();
                }
            }
        },
        active_settings_tab() {
            this.rebuild_tables?.();
        }
    },

    methods: {
        ...(settings_table_helpers.methods || {}),
        ...settings_api_methods,
        ...settings_ui_methods
    }
};
